import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Spinner } from "@/components/ui/spinner";
import { ReviewFilters } from "@/domains/settings/features/reviews/components/review-filters";
import { SalonReviewList } from "@/domains/settings/features/reviews/components/salon-review-list";
import { useReviewFilterIntent } from "@/domains/settings/features/reviews/hooks/use-review-filter-intent";
import { BackButton, BaseNavigationBar } from "@/lib/components/navigation";
import { PageContent } from "@/lib/components/page-content";
import { useSalonStore } from "@/lib/store/salon";
import { StarDistributionBar } from "../components/star-distribution-bar";
import { useSalonReviewStats } from "../hooks/use-salon-review-stats";

/**
 * Google Business reviews detail. These are synced from the salon's
 * connected Google Business profile and are repliable through the
 * dashboard — reply filter is kept on, no "+ Create" button (you can't
 * manually add a Google review).
 *
 * Previously named "GPA" (Google Profile Page) in early sketches; the
 * tech lead asked to standardize on "Google Business" matching the
 * branding used everywhere else in the product.
 *
 * If the salon hasn't connected a Google account yet, the list will be
 * empty; the empty-state in `SalonReviewList` already covers that case,
 * and the overview page's empty hint points users to settings.
 */
export function GoogleBusinessReviewsPage() {
  const { t } = useTranslation("reviews");
  const navigate = useNavigate();
  const { currentSalon } = useSalonStore();
  const { stats } = useSalonReviewStats("GOOGLE");

  useReviewFilterIntent();

  return (
    <>
      <BaseNavigationBar
        title={t("googleBusiness.title")}
        subtitle={t("googleBusiness.subtitle")}
        backButton={<BackButton onBack={() => navigate("/customer-service")} />}
      />
      <PageContent>
        {currentSalon ? (
          <div className="space-y-6">
            <Card>
              <CardContent className="p-5">
                {stats ? (
                  <div className="grid md:grid-cols-[auto_1fr] gap-4 md:gap-8 items-center">
                    <div>
                      <div className="text-4xl font-bold text-emerald-600 dark:text-emerald-400 tabular-nums">
                        {stats.count ? stats.averageRating.toFixed(1) : "—"}
                      </div>
                      <div className="text-sm text-muted-foreground mt-1">
                        {stats.count}{" "}
                        {stats.count === 1 ? t("overview.review") : t("overview.reviews")}
                      </div>
                    </div>
                    <StarDistributionBar distribution={stats.distribution} />
                  </div>
                ) : (
                  <div className="flex items-center justify-center py-4">
                    <Spinner size="sm" />
                  </div>
                )}
              </CardContent>
            </Card>

            <ReviewFilters />
            <SalonReviewList source="GOOGLE" />
          </div>
        ) : (
          <div className="text-sm text-muted-foreground">{t("overview.selectSalon")}</div>
        )}
      </PageContent>
    </>
  );
}
